import { useTranslation } from "react-i18next";
import { DeleteIcon, EditIcon } from "@/components/icons/action-icons";
import { useCallback, useRef, useState } from "react";
import {
  ConvoGroupLabel,
  ConvoHistoryItem,
  EmptyDataState,
  Skeletons,
} from "./convo-history-item";
import { dayLabel, groupByRecency } from "./conversation-groups";
import { cn } from "@/utils/cn";
import { ConversationQueries } from "./conversation-queries";
import { useScrollSentinel } from "./use-scroll-sentinel";
import { ChevronRight } from "lucide-react";
import {
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
} from "@/components/ui/input-group";
import { EllipsisVertical, Pin, Search, Share2, XIcon } from "lucide-react";
import { ShareChatDialog, type ShareLinkData } from "./share-chat-dialog";
import { useDebounceValue, useMediaQuery } from "usehooks-ts";
import { useConfirm } from "@/context/Confirm";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import toast from "react-hot-toast";
import type {
  IQuerySingleQuestion,
  ISingleConversationHistoryItem,
} from "@/types/(my-space)/ai-assistant.interface";
import { Spinner } from "@/components/ui/v2/spinner";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useAIAssistantContext } from "./context";
import { useLocale } from "@/hooks/useLocale";

interface Unwrappable<T> {
  unwrap: () => Promise<T>;
}
interface ApiResult<T> {
  message: string;
  data: T;
}
// RTK Query's mutation/lazy-query hooks return a readonly tuple — a mutable
// tuple type here would reject them (readonly isn't assignable to mutable).
type MutationHook<Arg, Res> = () => readonly [
  (arg: Arg) => Unwrappable<ApiResult<Res>>,
  { isLoading: boolean },
];
// useLazy*Query returns a 3-tuple ([trigger, result, lastPromiseInfo]) —
// tuple assignability is length-sensitive, so the third element must be here
// too even though it's unused.
type LazyQueryHook<Arg, Res> = () => readonly [
  (arg: Arg) => Unwrappable<ApiResult<Res>>,
  { isFetching: boolean },
  unknown,
];

/**
 * The RTK Query hooks this list needs, injected by the scope-specific wrapper
 * (my-space vs company use separate api slices, generated from separate
 * createApi() calls — their hook types carry different tag-name literal
 * unions, so a hand-written structural shape is used here instead of
 * `typeof <one slice's hook>`, which the other slice's hook fails to satisfy
 * even though both behave identically). The Rules of Hooks rule out picking
 * one api slice dynamically, so each caller calls its own hooks and hands the
 * hook *functions* here to be called internally, same as calling them directly.
 */
export interface ConversationHistoryApi {
  useConversations: (arg: { search: string }) => {
    data?: { pages: { data: ISingleConversationHistoryItem[] }[] };
    fetchNextPage: () => void;
    hasNextPage?: boolean;
    isFetching: boolean;
    isFetchingNextPage: boolean;
    isLoading: boolean;
  };
  useQuestionsBySession: (arg: { sessionId: string }) => {
    data?: { pages: { data: IQuerySingleQuestion[] }[] };
    fetchNextPage: () => void;
    hasNextPage?: boolean;
    isFetchingNextPage: boolean;
    isLoading: boolean;
  };
  usePinConversation: MutationHook<
    { sessionId?: string; pinned: boolean },
    void
  >;
  useRenameConversation: MutationHook<
    { sessionId?: string; newTitle: string },
    void
  >;
  useDeleteConversation: MutationHook<{ conversationIds: number[] }, void>;
  useLazyGetShareLink: LazyQueryHook<
    { sessionId: string },
    ShareLinkData | null
  >;
  useCreateShareLink: MutationHook<
    { sessionId: string; expiresInDays?: number },
    ShareLinkData
  >;
  useRevokeShareLink: MutationHook<{ sessionId: string }, null>;
}

interface ConversationsHistoryProps {
  api: ConversationHistoryApi;
  activeSessionId?: string;
}

export const ConversationsHistory = ({
  api,
  activeSessionId,
}: ConversationsHistoryProps) => {
  const { t } = useTranslation(["common", "my-space"]);
  const { formatDate } = useLocale();
  const [searchQuery, setSearchQuery] = useState("");
  const clearSearchQuery = () => setSearchQuery("");
  const [debouncedSearch] = useDebounceValue(searchQuery, 400);
  const { onSelectChat, onSelectQuery, isSidebarOpen, setIsSidebarOpen } =
    useAIAssistantContext();
  // Under 1200px the rail is an overlay drawer covering the transcript, so
  // picking something has to dismiss it — otherwise you select a chat and keep
  // staring at the list.
  const isSmallScreen = useMediaQuery("(max-width: 1200px)");
  const dismissOnSmallScreen = () => {
    if (isSmallScreen) setIsSidebarOpen(false);
  };
  // One open row at a time: the rail is narrow, and a single expansion keeps
  // both the scroll position and the number of in-flight fetches predictable.
  const [expandedSessionId, setExpandedSessionId] = useState<string | null>(
    null,
  );
  // Collapsed buckets, by label. Empty set = everything open, which is the
  // right default: you came here to see your chats.
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(
    new Set(),
  );
  const toggleGroup = (label: string) =>
    setCollapsedGroups((prev) => {
      const next = new Set(prev);
      if (!next.delete(label)) next.add(label);
      return next;
    });
  const scrollRef = useRef<HTMLDivElement>(null);
  const sentinelRef = useRef<HTMLDivElement>(null);

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetching,
    isFetchingNextPage,
    isLoading: isFirstFetch,
  } = api.useConversations({ search: debouncedSearch });
  const conversations = data?.pages.flatMap((page) => page.data) ?? [];

  const loadMore = useCallback(() => {
    fetchNextPage();
  }, [fetchNextPage]);
  useScrollSentinel({
    rootRef: scrollRef,
    targetRef: sentinelRef,
    enabled: !!hasNextPage && !isFetchingNextPage,
    onReach: loadMore,
  });

  return (
    <div className="flex flex-col max-h-full gap-2 overflow-y-hidden">
      <div className="bg-surface sticky top-0 z-20 shrink-0 pb-1">
        <InputGroup>
          <InputGroupInput
            value={searchQuery}
            placeholder={t("common:ai_assistant.search_placeholder")}
            aria-label={t("common:ai_assistant.search_chats")}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={(e) => {
              // First Escape clears the search; marking it handled keeps a
              // surrounding panel (the Maya sidebar's chat list) from closing.
              if (e.key === "Escape" && searchQuery) {
                e.preventDefault();
                clearSearchQuery();
              }
            }}
            onClick={() =>
              !isSidebarOpen ? setIsSidebarOpen(true) : undefined
            }
          />
          <InputGroupAddon>
            <Search />
          </InputGroupAddon>
          <InputGroupAddon align="inline-end">
            {isFetching ? (
              <Spinner size="sm" />
            ) : !isFetching && searchQuery ? (
              <button
                type="button"
                onClick={clearSearchQuery}
                aria-label={t("common:buttons.clear")}
                title={t("common:buttons.clear")}
              >
                <XIcon size={18} />
              </button>
            ) : null}
          </InputGroupAddon>
        </InputGroup>
        {debouncedSearch && !isFirstFetch && (
          <p className="text-muted-foreground mb-0! px-1 pt-1 text-[11px]">
            {t("common:ai_assistant.matches_count", {
              count: conversations.length,
            })}
          </p>
        )}
      </div>
      <div
        ref={scrollRef}
        className="h-full overflow-y-auto flex flex-col scrollbar-hidden"
      >
        {isFirstFetch ? (
          <Skeletons />
        ) : conversations.length === 0 ? (
          <EmptyDataState
            label={
              searchQuery
                ? t("common:ai_assistant.no_matches")
                : t("common:ai_assistant.no_conversations_yet")
            }
            hint={
              searchQuery
                ? t("common:ai_assistant.try_different_search")
                : t("common:ai_assistant.start_a_chat_shows_here")
            }
          />
        ) : (
          <div className="flex flex-col">
            {groupByRecency(
              conversations,
              (conv) => conv.createdAt,
              t,
              formatDate,
              (conv) => !!conv.pinned,
            ).map((group) => {
              const isCollapsed = collapsedGroups.has(group.label);
              return (
                <div key={group.label} className="flex flex-col">
                  <ConvoGroupLabel
                    count={group.items.length}
                    tone={
                      group.label === t("my-space:ui.pinned_group")
                        ? "pinned"
                        : "default"
                    }
                    isCollapsed={isCollapsed}
                    onToggle={() => toggleGroup(group.label)}
                  >
                    {group.label}
                  </ConvoGroupLabel>
                  {!isCollapsed &&
                    group.items.map((conv) => {
                      const isExpanded = expandedSessionId === conv.sessionId;
                      const panelId = `convo-prompts-${conv.sessionId}`;
                      return (
                        <div key={conv.id} className="flex flex-col">
                          <ConvoHistoryItem
                            convTitle={conv.title}
                            isPinned={conv.pinned}
                            isActive={conv.sessionId === activeSessionId}
                            actions={
                              <ConvActions
                                api={api}
                                conversation={conv}
                                isActive={conv.sessionId === activeSessionId}
                              />
                            }
                            subtitle={dayLabel(conv.createdAt, t, formatDate)}
                            onClick={() => {
                              onSelectChat?.(conv.sessionId);
                              dismissOnSmallScreen();
                            }}
                            leadingSlot={
                              <button
                                type="button"
                                aria-expanded={isExpanded}
                                aria-controls={panelId}
                                aria-label={
                                  isExpanded
                                    ? t("common:ai_assistant.hide_prompts")
                                    : t("common:ai_assistant.show_prompts")
                                }
                                onClick={() =>
                                  setExpandedSessionId((prev) =>
                                    prev === conv.sessionId
                                      ? null
                                      : conv.sessionId,
                                  )
                                }
                                className="text-muted-foreground hover:text-foreground grid size-6 shrink-0 place-items-center rounded-xs"
                              >
                                <ChevronRight
                                  size={14}
                                  className={cn(
                                    "rtl-flip transition-transform duration-150",
                                    isExpanded && "rotate-90",
                                  )}
                                />
                              </button>
                            }
                          />
                          {isExpanded && (
                            <ConversationQueries
                              sessionId={conv.sessionId}
                              panelId={panelId}
                              useQuestionsBySession={api.useQuestionsBySession}
                              onSelectQuery={(sid, questionId) => {
                                const result = onSelectQuery?.(sid, questionId);
                                dismissOnSmallScreen();
                                return result;
                              }}
                            />
                          )}
                        </div>
                      );
                    })}
                </div>
              );
            })}
            <div ref={sentinelRef} aria-hidden="true" className="h-px" />
            {isFetchingNextPage && (
              <div className="flex justify-center py-3">
                <Spinner size="sm" />
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const ConvActions = ({
  api,
  conversation,
  isActive,
}: {
  api: ConversationHistoryApi;
  conversation: ISingleConversationHistoryItem;
  /** This is the chat currently open — deleting it starts a new one. */
  isActive: boolean;
}) => {
  const { t } = useTranslation(["common"]);
  const { startNewChat } = useAIAssistantContext();
  const { id, sessionId, pinned } = conversation;
  const [pinConversation, { isLoading: isPinningConv }] =
    api.usePinConversation();
  const [renameConversation, { isLoading: isRenamingConv }] =
    api.useRenameConversation();
  const [deleteConversation, { isLoading: isDeletingConv }] =
    api.useDeleteConversation();
  const [fetchShareLink, { isFetching: isFetchingShare }] =
    api.useLazyGetShareLink();
  const [createShareLink, { isLoading: isCreatingShare }] =
    api.useCreateShareLink();
  const [revokeShareLink, { isLoading: isRevokingShare }] =
    api.useRevokeShareLink();
  const confirm = useConfirm();
  const [showRenamingDialog, setShowRenamingDialog] = useState(false);
  const [newName, setNewName] = useState(conversation.title);
  const [showShareDialog, setShowShareDialog] = useState(false);
  const [existingShare, setExistingShare] = useState<ShareLinkData | null>(
    null,
  );
  const isLoading = isPinningConv || isRenamingConv || isDeletingConv;

  const openShareDialog = async () => {
    setExistingShare(null);
    setShowShareDialog(true);
    try {
      const res = await fetchShareLink({ sessionId }).unwrap();
      setExistingShare(res?.data ?? null);
    } catch {
      setExistingShare(null);
    }
  };

  const handleCreateShare = async (
    expiresInDays: number | null,
  ): Promise<ShareLinkData | null> => {
    const res = await createShareLink({
      sessionId,
      ...(expiresInDays != null ? { expiresInDays } : {}),
    }).unwrap();
    return res?.data ?? null;
  };

  const handleRevokeShare = async () => {
    await revokeShareLink({ sessionId }).unwrap();
  };

  return (
    <div className="flex items-center">
      <button
        aria-label={t("common:ai_assistant.share_conversation")}
        title={t("common:ai_assistant.share")}
        onClick={(e) => {
          e.stopPropagation();
          openShareDialog();
        }}
        className="text-muted-foreground hover:bg-surface hover:text-foreground grid size-7 place-items-center rounded-md transition-colors"
      >
        <Share2 size={15} />
      </button>
      <DropdownMenu>
        {isLoading ? (
          <Spinner size="sm" wrapperClassName="size-min" />
        ) : (
          <DropdownMenuTrigger asChild disabled={isLoading}>
            <button
              aria-label={t("common:ai_assistant.conversation_options")}
              className="text-muted-foreground hover:bg-surface hover:text-foreground grid size-7 place-items-center rounded-md transition-colors"
            >
              <EllipsisVertical size={16} />
            </button>
          </DropdownMenuTrigger>
        )}
        <DropdownMenuContent className="w-max rounded-xs" align="end">
          <DropdownMenuGroup>
            <Dialog
              open={showRenamingDialog}
              onOpenChange={setShowRenamingDialog}
            >
              <DialogTrigger
                onClick={(e) => {
                  e.stopPropagation();
                  setShowRenamingDialog(true);
                }}
                asChild
              >
                <DropdownMenuItem
                  className="cursor-pointer"
                  disabled={isRenamingConv}
                  onSelect={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                  }}
                >
                  <EditIcon className="text-warning" /> {t("common:ai_assistant.rename_chat")}
                </DropdownMenuItem>
              </DialogTrigger>
              <DialogContent
                // No description element — opt out of Radix's warning rather
                // than pointing at an id that doesn't exist.
                aria-describedby={undefined}
                onClick={(e) => e.stopPropagation()}
              >
                <DialogHeader>
                  <DialogTitle>{t("common:ai_assistant.rename_this_conversation")}</DialogTitle>
                </DialogHeader>
                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    renameConversation({ sessionId, newTitle: newName })
                      .unwrap()
                      .then((res) => {
                        toast.success(res?.message);
                        setShowRenamingDialog(false);
                      })
                      .catch((e) =>
                        toast.error(e?.message ?? e?.data?.message),
                      );
                  }}
                >
                  <Input
                    required
                    maxLength={500}
                    placeholder={t("common:ai_assistant.new_name_placeholder")}
                    defaultValue={conversation.title}
                    onChange={(e) => setNewName(e.target.value)}
                  />
                  <DialogFooter className="mt-2">
                    <Button
                      type="reset"
                      variant="outline"
                      onClick={() => setShowRenamingDialog(false)}
                    >
                      {t("common:buttons.cancel")}
                    </Button>
                    <Button
                      type="submit"
                      isLoading={isRenamingConv}
                      disabled={conversation.title === newName}
                    >
                      {t("common:buttons.rename")}
                    </Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
            <DropdownMenuItem
              className="cursor-pointer"
              disabled={isPinningConv}
              onClick={(e) => {
                e.stopPropagation();
                pinConversation({ pinned: !pinned, sessionId })
                  .unwrap()
                  .then((res) => toast.success(res?.message))
                  .catch((e) => toast.error(e?.message ?? e?.data?.message));
              }}
            >
              <Pin className="text-info" />{" "}
              {pinned
                ? t("common:ai_assistant.unpin_conversation")
                : t("common:ai_assistant.pin_conversation")}
            </DropdownMenuItem>
            <DropdownMenuItem
              className="cursor-pointer"
              onClick={(e) => {
                e.stopPropagation();
                confirm({
                  title: t("common:ai_assistant.delete_conversation"),
                  msg: t("common:ai_assistant.delete_conversation_confirm"),
                  onConfirm: async () => {
                    const toastId = toast.loading(t("common:states.deleting"));
                    await deleteConversation({ conversationIds: [id] })
                      .unwrap()
                      .then((res) => {
                        toast.success(res?.message, { id: toastId });
                        // Only the open chat's deletion leaves nothing to
                        // show; deleting another one must not reset (or cut
                        // off a reply in) the chat on screen.
                        if (isActive && startNewChat) startNewChat();
                      })
                      .catch((e) =>
                        toast.error(e?.message ?? e?.data?.message, {
                          id: toastId,
                        }),
                      );
                  },
                });
              }}
            >
              <span className="text-danger flex gap-2 items-center">
                <DeleteIcon className="text-danger" /> {t("common:buttons.delete")}
              </span>
            </DropdownMenuItem>
          </DropdownMenuGroup>
        </DropdownMenuContent>
      </DropdownMenu>
      <ShareChatDialog
        open={showShareDialog}
        onOpenChange={setShowShareDialog}
        conversationTitle={conversation.title}
        existing={existingShare}
        isLoading={isFetchingShare}
        isCreating={isCreatingShare}
        isRevoking={isRevokingShare}
        onCreate={handleCreateShare}
        onRevoke={handleRevokeShare}
      />
    </div>
  );
};
